'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import MarketingLayout from '@/components/MarketingLayout';

const versions = [
  {
    id: 'mechanical',
    name: 'Mechanical Standard',
    tagline: 'Field-ready equalization',
    desc: 'The core BridgeFlow™ MTE platform. Rugged, field-proven simultaneous multi-bolt tightening with automatic torque redistribution. No electronics — pure mechanical integrity.',
    features: [
      'Simultaneous multi-bolt tightening',
      'Resistance-based torque equilibrium',
      'One stroke operation',
      'ASME B16.5 compatible — no flange modification',
      'Automatic driving head alignment',
      '<5% preload variation',
    ],
    badge: 'Entry',
    badgeColor: 'bg-[#1E293B] text-[#94A3B8] border-[#334155]',
    highlight: false,
  },
  {
    id: 'display',
    name: 'With Torque Display',
    tagline: 'Real-time visual monitoring',
    desc: 'All the mechanical advantages of the Standard, plus real-time visual torque monitoring so operators can confirm equalization is occurring during the tightening stroke.',
    features: [
      'Everything in Mechanical Standard',
      'Real-time torque display',
      'Live visual confirmation of equalization',
      'On-site torque verification',
      'Operator confidence monitoring',
    ],
    badge: 'Popular',
    badgeColor: 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/30',
    highlight: false,
  },
  {
    id: 'digital',
    name: 'Digital Torque Recording',
    tagline: 'Full digital integrity auditing',
    desc: 'The complete BridgeFlow™ MTE platform. Per-head real-time torque monitoring, wireless data connection, precise calibration, and final torque recording for digital QC documentation.',
    features: [
      'Everything in Torque Display',
      'Per-head real-time torque monitoring',
      'Wireless data connection',
      'More precise real-time torque calibration',
      'Final torque recording per driving head',
      'Digital QC integrity audit trail',
      'Compliance documentation output',
    ],
    badge: 'Full Platform',
    badgeColor: 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]/30',
    highlight: true,
  },
];

const specs = [
  { label: 'Flange Standard', value: 'ASME B16.5', note: 'No modification required' },
  { label: 'Preload Variation', value: '<5%', note: 'Uniform across all bolts' },
  { label: 'Operation Mode', value: 'One Stroke', note: 'Single input, multiple outputs' },
  { label: 'Tightening Method', value: 'Simultaneous', note: 'All bolts at once' },
  { label: 'Torque Mechanism', value: 'Resistance-Based Equilibrium', note: 'Dynamic redistribution' },
  { label: 'Time per Flange', value: '~8 minutes', note: 'vs. 45 min traditional' },
  { label: 'Speed Improvement', value: 'Up to 90% faster', note: 'vs. hydraulic torque wrenches' },
  { label: 'Passes Required', value: '1 (one stroke)', note: 'vs. 3–5 traditional passes' },
];

const comparisonRows = [
  { feature: 'Tightening method', traditional: 'Sequential, bolt-by-bolt', bridgeflow: 'Simultaneous, all bolts at once' },
  { feature: 'Star pattern required', traditional: 'Yes — mandatory', bridgeflow: 'Eliminated entirely' },
  { feature: 'Torque distribution', traditional: 'Operator-controlled sequence', bridgeflow: 'Automatic mechanical redistribution' },
  { feature: 'Flange load uniformity', traditional: 'Depends on operator technique', bridgeflow: 'Dynamic equalization across flange' },
  { feature: 'Number of passes', traditional: '3–5 passes + verification', bridgeflow: '1 stroke' },
  { feature: 'Time per flange', traditional: '45 minutes', bridgeflow: '8 minutes' },
  { feature: 'Operator skill dependency', traditional: 'High', bridgeflow: 'Low — system-embedded integrity' },
  { feature: 'Preload variation', traditional: 'Operator-dependent', bridgeflow: '<5%' },
  { feature: 'Digital documentation', traditional: 'Available with digital pumps', bridgeflow: 'Per-head recording (Digital version)' },
  { feature: 'Flange distortion risk', traditional: 'Higher if sequence inconsistent', bridgeflow: 'Lower — simultaneous loading' },
];

export default function ProductPage() {
  const [activeVersion, setActiveVersion] = useState('digital');

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0F1A]" />
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(249,115,22,0.06) 0%, transparent 60%)',
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">The Product</span>
          <h1 className="text-display text-[#F1F5F9] mt-4 mb-6 max-w-3xl mx-auto">
            BridgeFlow™ MTE System — Not a Torque Wrench. A Flange Integrity Platform.
          </h1>
          <p className="text-[#64748B] text-lg leading-relaxed max-w-2xl mx-auto mb-8">
            Patented Mechanical Torque Equalization technology that simultaneously tightens all flange bolts in a single stroke, achieving uniform preload through resistance-based equilibrium — without operator skill dependency.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {['ASME B16.5 Compatible', 'Patented Technology', 'No Flange Modification', 'One Stroke Operation']?.map((b) => (
              <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E293B] border border-[#334155] text-[#94A3B8] text-xs font-medium">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2 2 4-4" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>
      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-[#060B14] border-y border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-xl mb-12">
            <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Core Mechanism</span>
            <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Resistance-Based Torque Equilibrium</h2>
            <p className="text-[#64748B] leading-relaxed">
              BridgeFlow™ MTE uses mechanical synchronization to route torque toward the loosest bolt first — automatically redistributing load until all bolts reach equilibrium. No operator judgment required.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {[
              { step: '01', title: 'Position System', desc: 'Place BridgeFlow™ MTE around the flange. Compatible with ASME B16.5 — zero flange modification.' },
              { step: '02', title: 'Encircle & Align', desc: 'System encircles the pipe. Driving heads automatically align to each bolt position.' },
              { step: '03', title: 'Dynamic Redistribution', desc: 'Single input engages all bolts simultaneously. Torque flows to the loosest bolt first until equilibrium.' },
              { step: '04', title: 'Verified Integrity', desc: 'All bolts reach uniform preload in one stroke. Digital version records final torque per head.' },
            ]?.map((s, i) => (
              <div key={s?.step} className="relative rounded-2xl border border-[#1E293B] bg-[#111827] p-6 hover:border-[#F97316]/30 transition-all duration-300 group">
                {i < 3 && <div className="hidden xl:block absolute top-10 -right-2.5 w-5 h-0.5 bg-[#F97316]/30 z-10" />}
                <div className="font-mono text-[#F97316]/25 text-5xl font-bold mb-4 group-hover:text-[#F97316]/40 transition-colors">{s?.step}</div>
                <h3 className="text-[#F1F5F9] font-bold text-base mb-2">{s?.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{s?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Technical Specs */}
      <section id="specs" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Technical Specifications</span>
              <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Engineered around flange behavior</h2>
              <p className="text-[#64748B] leading-relaxed mb-6">
                Every specification is derived from flange physics — not just bolt tightening. The result is a system that achieves what sequential methods cannot: true uniform preload.
              </p>
              <Link href="/contact#demo" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#F97316] text-white font-semibold hover:bg-[#EA580C] transition-all duration-150 shadow-lg shadow-orange-500/20 active:scale-95 text-sm">
                Request Technical Datasheet
              </Link>
            </div>
            <div className="lg:col-span-8">
              <div className="rounded-2xl border border-[#1E293B] bg-[#111827] overflow-hidden">
                {specs?.map((spec, i) => (
                  <div key={spec?.label} className={`flex items-center justify-between px-6 py-4 ${i < specs?.length - 1 ? 'border-b border-[#1E293B]' : ''} hover:bg-[#1E293B]/30 transition-colors`}>
                    <div>
                      <p className="text-[#94A3B8] text-sm font-medium">{spec?.label}</p>
                      <p className="text-[#475569] text-xs mt-0.5">{spec?.note}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[#F97316] font-bold text-base">{spec?.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Three Versions */}
      <section id="versions" className="py-20 lg:py-28 bg-[#060B14] border-y border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Three Versions</span>
            <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Choose your level of digital integrity</h2>
            <p className="text-[#64748B] leading-relaxed">
              All three versions deliver simultaneous multi-bolt equalization. The difference is how much digital visibility and documentation you need.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {versions?.map((v) => (
              <div
                key={v?.id}
                className={`relative rounded-2xl border p-7 transition-all duration-300 cursor-pointer ${
                  v?.highlight
                    ? 'border-[#F97316]/40 bg-gradient-to-br from-[#111827] to-[#0F172A] shadow-xl shadow-orange-500/10'
                    : 'border-[#1E293B] bg-[#111827] hover:border-[#F97316]/20'
                }`}
                onClick={() => setActiveVersion(v?.id)}
              >
                {v?.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full bg-[#F97316] text-white text-xs font-bold shadow-lg shadow-orange-500/30">Recommended</span>
                  </div>
                )}
                <div className="mb-5">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md border text-xs font-semibold ${v?.badgeColor}`}>{v?.badge}</span>
                </div>
                <h3 className="text-[#F1F5F9] font-bold text-xl mb-1">{v?.name}</h3>
                <p className="text-[#F97316] text-sm font-medium mb-4">{v?.tagline}</p>
                <p className="text-[#64748B] text-sm leading-relaxed mb-6">{v?.desc}</p>
                <ul className="space-y-2.5">
                  {v?.features?.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <svg className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8l3 3 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span className="text-[#94A3B8]">{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-6 border-t border-[#1E293B]">
                  <Link
                    href="/contact#demo"
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95 ${
                      v?.highlight
                        ? 'bg-[#F97316] text-white hover:bg-[#EA580C] shadow-lg shadow-orange-500/20'
                        : 'bg-[#1E293B] text-[#F1F5F9] hover:bg-[#263348] border border-[#334155]'
                    }`}
                  >
                    Request Demo
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Comparison Table */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#F97316] text-xs font-semibold uppercase tracking-widest">Competitive Comparison</span>
            <h2 className="text-display text-[#F1F5F9] mt-3 mb-4">Traditional hydraulic bolting vs. BridgeFlow™ MTE</h2>
            <p className="text-[#64748B] leading-relaxed">
              Traditional systems improve torque delivery. BridgeFlow transforms load equalization.
            </p>
          </div>

          <div className="rounded-2xl border border-[#1E293B] bg-[#111827] overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-3 bg-[#0A0F1A] border-b border-[#1E293B]">
              <div className="px-6 py-4 text-[#64748B] text-xs font-semibold uppercase tracking-wider">Feature</div>
              <div className="px-6 py-4 text-center">
                <span className="text-[#EF4444] text-xs font-semibold uppercase tracking-wider">Traditional Hydraulic</span>
              </div>
              <div className="px-6 py-4 text-center">
                <span className="text-[#F97316] text-xs font-semibold uppercase tracking-wider">BridgeFlow™ MTE</span>
              </div>
            </div>
            {comparisonRows?.map((row, i) => (
              <div key={row?.feature} className={`grid grid-cols-3 ${i < comparisonRows?.length - 1 ? 'border-b border-[#1E293B]' : ''} hover:bg-[#1E293B]/20 transition-colors`}>
                <div className="px-6 py-4 text-[#94A3B8] text-sm font-medium">{row?.feature}</div>
                <div className="px-6 py-4 text-center">
                  <span className="text-[#64748B] text-sm">{row?.traditional}</span>
                </div>
                <div className="px-6 py-4 text-center">
                  <span className="text-[#22C55E] text-sm font-medium">{row?.bridgeflow}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 bg-[#060B14] border-t border-[#1E293B]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-display text-[#F1F5F9] mb-4">Ready to see BridgeFlow™ MTE in action?</h2>
          <p className="text-[#64748B] text-lg mb-8">Talk to our team about your flange scope, shutdown schedule, and which version fits your operation.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="mailto:info@irongatesupply.com?subject=Request%20a%20demo" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#F97316] text-white font-semibold hover:bg-[#EA580C] transition-all duration-150 shadow-xl shadow-orange-500/25 active:scale-95">
              Request a Demo
            </a>
            <Link href="/roi-calculator" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#1E293B] text-[#F1F5F9] font-semibold hover:bg-[#263348] transition-all duration-150 border border-[#334155] active:scale-95">
              Calculate ROI
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
